import {
  LitElement,
  html,
  nothing,
  type CSSResultGroup,
  type TemplateResult,
} from "lit";
import { property } from "lit/decorators.js";
import { ContextProvider } from "@lit/context";
import {
  EmbedConfigError,
  fetchEmbedConfig,
  getEmbeddedApiClients,
} from "../../data/embedded-api";
import type { ComponentType, EmbedConfig } from "../../config/types";
import { baseStyles, themeToCssVars } from "../../theme/theme";
import { zipperLogoGrey } from "../../theme/zipper-logo";
import {
  createPublicEmbedRuntime,
  embedRuntimeContext,
  type EmbedRuntime,
} from "../../runtime/context";
import { LitActorController } from "../../state/lit-actor-controller";
import { createEmbedConfigMachine } from "../../state/embed-config.machine";

/** Zipper's marketing site — target of the "Powered by Zipper" footer link.
 * Mirrors the pattern in libs/shared-ux/src/site-builder/components/ScheduleBlockBranding.tsx. */
const ZIPPER_MARKETING_URL = "https://www.joinzipper.com/";

/**
 * Base class for every Zipper embed.
 *
 * Responsibilities common to all components:
 *  - read the `instance` attribute (the opaque config id)
 *  - fetch the instance config from the public endpoint on connect
 *  - apply theme tokens to the shadow host
 *  - render shared loading / error / disabled states
 *  - render a "Powered by Zipper" footer
 *
 * Subclasses implement {@link renderContent} with their actual UI and declare
 * their {@link expectedType} so a mis-typed instance id fails loudly.
 */
export abstract class ZipperEmbedElement extends LitElement {
  static styles: CSSResultGroup = baseStyles;

  private runtimeValue = createPublicEmbedRuntime();
  private readonly runtimeProvider = new ContextProvider(this, {
    context: embedRuntimeContext,
    initialValue: this.runtimeValue,
  });

  /**
   * Host-provided capabilities. React assigns this as a property; descendants
   * receive the same object through Lit context.
   */
  @property({ attribute: false })
  set runtime(value: EmbedRuntime | undefined) {
    if (value && Object.is(value, this.runtimeValue)) return;
    const previous = this.runtimeValue;
    this.runtimeValue = value ?? createPublicEmbedRuntime();
    this.runtimeProvider.setValue(this.runtimeValue);
    this.requestUpdate("runtime", previous);
    if (this.isConnected && this.instance) this.load();
  }

  get runtime(): EmbedRuntime {
    return this.runtimeValue;
  }

  /** Opaque config-instance id, e.g. <zipper-schedule instance="abc123">. */
  @property({ type: String }) instance = "";

  private configurationValue: EmbedConfig | undefined;

  /**
   * Trusted host configuration. Dashboard React assigns this as a JavaScript
   * property so it never needs a public embed instance or serialized config.
   */
  @property({ attribute: false })
  set configuration(value: EmbedConfig | undefined) {
    if (Object.is(value, this.configurationValue)) return;
    const previous = this.configurationValue;
    this.configurationValue = value;
    this.requestUpdate("configuration", previous);
    if (value) {
      this.provideConfig(value);
    } else if (this.isConnected) {
      this.load();
    }
  }

  get configuration(): EmbedConfig | undefined {
    return this.configurationValue;
  }

  private readonly configActor = new LitActorController(
    this,
    createEmbedConfigMachine((instanceId, signal) => {
      if (!instanceId) {
        return Promise.reject(
          new EmbedConfigError('Missing "instance" attribute'),
        );
      }
      return fetchEmbedConfig(
        instanceId,
        this.runtime.api ?? getEmbeddedApiClients(),
        signal,
      );
    }),
    {},
    (previous, next) => {
      const changes: [PropertyKey, unknown][] = [];
      if (previous.context.config !== next.context.config) {
        changes.push(["config", previous.context.config]);
      }
      if (previous.context.error !== next.context.error) {
        changes.push(["error", previous.context.error]);
      }
      if (
        previous.matches("loading") !== next.matches("loading") ||
        previous.matches("idle") !== next.matches("idle")
      ) {
        changes.push([
          "loading",
          previous.matches("idle") || previous.matches("loading"),
        ]);
      }
      return changes;
    },
  );

  protected get config(): EmbedConfig | null {
    return this.configActor.snapshot.context.config;
  }

  /**
   * Allows a trusted host to seed an already-loaded configuration through the
   * same actor used by network loading. This also preserves the component
   * test seam without creating a second source of truth.
   */
  protected set config(config: EmbedConfig) {
    this.provideConfig(config);
  }

  protected get loading(): boolean {
    return (
      this.configActor.snapshot.matches("idle") ||
      this.configActor.snapshot.matches("loading")
    );
  }

  protected get error(): string | null {
    return this.configActor.snapshot.context.error;
  }

  /** The componentType this element expects the fetched config to declare. */
  protected abstract get expectedType(): ComponentType;

  /** Subclasses render their UI here once config has loaded successfully. */
  protected abstract renderContent(config: EmbedConfig): TemplateResult;

  connectedCallback(): void {
    super.connectedCallback();
    this.load();
  }

  updated(changed: Map<string, unknown>): void {
    if (
      changed.has("instance") &&
      this.configActor.snapshot.context.instanceId !== this.instance
    ) {
      this.load();
    }
    if (this.config && changed.has("config")) {
      this.applyTheme(this.config);
    }
    if (this.error && changed.has("error")) {
      console.error("[zipper-embed]", this.error);
    }
  }

  private load(): void {
    if (this.configurationValue) {
      this.provideConfig(this.configurationValue);
      return;
    }
    if (!this.instance) {
      this.configActor.send({
        type: "config.load",
        instanceId: "",
        expectedType: this.expectedType,
      });
      return;
    }
    this.configActor.send({
      type: "config.load",
      instanceId: this.instance,
      expectedType: this.expectedType,
    });
  }

  private provideConfig(config: EmbedConfig): void {
    this.configActor.send({
      type: "config.provided",
      config,
      expectedType: this.expectedType,
    });
  }

  /** Theme properties this element set last time, so stale ones are removed. */
  private themeProps: string[] = [];

  /**
   * Set each theme token individually rather than replacing the `style`
   * attribute: hosts put their own properties inline on the element (e.g.
   * `style="--zui-sticky-offset: 64px"`), and components write measured ones
   * (`--zui-sticky-h`) — overwriting the whole attribute erased both.
   */
  private applyTheme(config: EmbedConfig): void {
    const next = themeToCssVars(config.theme, config.options?.buttonShape)
      .split(";")
      .map((decl) => {
        const i = decl.indexOf(":");
        return [decl.slice(0, i), decl.slice(i + 1)] as const;
      });
    const names = next.map(([name]) => name);
    for (const name of this.themeProps) {
      if (!names.includes(name)) this.style.removeProperty(name);
    }
    for (const [name, value] of next) this.style.setProperty(name, value);
    this.themeProps = names;
  }

  render(): TemplateResult {
    if (this.loading) {
      return this.renderSkeleton();
    }
    if (this.error || !this.config) {
      // Fail quietly on the host page; surface details in the console only.
      return html`<div class="zui-state" part="error" role="alert">
        ${this.error ?? "Unavailable"}
      </div>`;
    }
    return html` ${this.renderContent(this.config)} ${this.renderPoweredBy()} `;
  }

  /**
   * Shimmering placeholder shown while the config/data fetch is in flight.
   * Shaped like a schedule card (day-pill strip + a few thumbnail rows) so it
   * doesn't jarringly pop to the real layout once loaded.
   */
  protected renderSkeleton(): TemplateResult {
    return html`<div
      class="zui-skeleton"
      part="loading"
      aria-busy="true"
      aria-label="Loading"
    >
      <div class="zui-skel-strip">
        ${[0, 1, 2, 3, 4, 5, 6].map(
          () => html`<div class="zui-skel-pill"></div>`,
        )}
      </div>
      <div class="zui-skel-rows">
        ${[0, 1, 2].map(
          () => html`
            <div class="zui-skel-row">
              <div class="zui-skel-thumb"></div>
              <div class="zui-skel-lines">
                <div class="zui-skel-line zui-skel-line-lg"></div>
                <div class="zui-skel-line zui-skel-line-sm"></div>
              </div>
            </div>
          `,
        )}
      </div>
    </div>`;
  }

  protected renderPoweredBy(): TemplateResult | typeof nothing {
    return html`<a
      class="zui-powered"
      part="powered"
      href=${ZIPPER_MARKETING_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span>Powered by</span>${zipperLogoGrey}
    </a>`;
  }
}
